import React from 'react';
import {
  Check,
  X,
  Minus,
  Zap,
  Database,
  Clock,
  Code2,
  Bot,
  TrendingUp,
  AlertTriangle,
  FileText,
  Users,
  DollarSign,
  BarChart3,
  Globe,
  Layers,
  Search,
  Shield,
  CreditCard,
  Building2,
  Newspaper,
} from 'lucide-react';
import FAQ from '@/components/landing/pricing/FAQ';
import {
  Section,
  H2,
  H3,
  P,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  IconCell,
  Callout,
  CodeBlock,
  CTALink,
  Chart,
  ChartTitle,
  ChartRow,
  ChartLabel,
  ChartBarTrack,
  ChartBar,
  ChartValue,
  TimelineContainer,
  TimelineRow,
  TimelineLabel,
  TimelineTrack,
  TimelineFill,
  StatGrid,
  StatCard,
  VerdictBox,
} from '@/pages/styles/ComparisonStyles';

export const meta = {
  title: 'Finnhub vs AxionQuant Stock Data API Comparison | AxionQuant',
  description:
    'Finnhub vs AxionQuant: a head-to-head comparison of two financial data APIs covering real-time quotes, fundamentals, alternative data, rate limits, and pricing.',
  canonicalUrl: 'https://axionquant.com/compare/finnhub-vs-axionquant',
};

const Yes = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <Check size={18} color="#16a34a" strokeWidth={3} />
    {children}
  </IconCell>
);

const No = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <X size={18} color="#dc2626" strokeWidth={3} />
    {children}
  </IconCell>
);

const Partial = ({ children }: { children?: React.ReactNode }) => (
  <IconCell $label={!!children}>
    <Minus size={18} color="#f59e0b" strokeWidth={3} />
    {children}
  </IconCell>
);

export default function FinnhubVsAxionquantContent() {
  return (
    <>
      <Section>
        <H2 className="serif">Finnhub vs AxionQuant</H2>
        <P>
          Finnhub has earned a reputation as one of the most developer-friendly
          financial data APIs on the market. Its free tier offers 60 requests
          per minute with access to US real-time quotes, company profiles, and
          basic financial metrics, making it a popular starting point for
          fintech MVPs and quantitative research projects.
        </P>
        <P>
          AxionQuant takes a different approach. Rather than optimizing for
          the largest possible free tier, it optimizes for the complete modern
          data workflow: price data, fundamentals, alternative data, and AI
          agent integration, all behind a single API key and SDK. This
          comparison examines both platforms across the dimensions that matter
          most when you are choosing a data provider for a project that needs
          to scale.
        </P>

        <StatGrid>
          <StatCard>
            <div className="value">60</div>
            <div className="label">
              Finnhub free tier requests per minute
            </div>
          </StatCard>
          <StatCard>
            <div className="value">100+</div>
            <div className="label">
              MCP tools exposed by AxionQuant
            </div>
          </StatCard>
          <StatCard>
            <div className="value">$3,500</div>
            <div className="label">
              Monthly cost of Finnhub All-in-One tier
            </div>
          </StatCard>
          <StatCard>
            <div className="value">14</div>
            <div className="label">
              Data categories covered by AxionQuant in a single API
            </div>
          </StatCard>
        </StatGrid>
      </Section>

      <Section>
        <H2 className="serif">At a Glance</H2>
        <Table>
          <THead>
            <TR>
              <TH>Feature</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Free Tier</strong>
              </TD>
              <TD>Generous free tier designed for real development work</TD>
              <TD>60 requests per minute, US quotes and selected company data</TD>
            </TR>
            <TR>
              <TD>
                <strong>Paid Entry Point</strong>
              </TD>
              <TD>Transparent, competitive tiers</TD>
              <TD>$11.99 per month Starter, $49.99 per month Professional</TD>
            </TR>
            <TR>
              <TD>
                <strong>Enterprise Tier</strong>
              </TD>
              <TD>Published on pricing page</TD>
              <TD>$3,500 per month All-in-One</TD>
            </TR>
            <TR>
              <TD>
                <strong>Rate Limit Model</strong>
              </TD>
              <TD>Per-second and daily budgets with standard headers</TD>
              <TD>60 per minute free, 300+ per minute paid</TD>
            </TR>
            <TR>
              <TD>
                <strong>Asset Classes</strong>
              </TD>
              <TD>Stocks, crypto, forex, futures, indices, ETFs, economics</TD>
              <TD>Stocks, ETFs, forex, crypto, indices, bonds</TD>
            </TR>
            <TR>
              <TD>
                <strong>Alternative Data</strong>
              </TD>
              <TD>SEC filings, insider trading, ESG, credit ratings, sentiment, transcripts</TD>
              <TD>Congressional trading, lobbying, ESG scores, social sentiment, transcripts</TD>
            </TR>
            <TR>
              <TD>
                <strong>AI and LLM Integration</strong>
              </TD>
              <TD>Official MCP server with 100+ tools plus TypeScript SDK</TD>
              <TD>Hosted MCP server with 70 tools</TD>
            </TR>
            <TR>
              <TD>
                <strong>SDK Support</strong>
              </TD>
              <TD>Official TypeScript, JavaScript, Python</TD>
              <TD>Official Python, JavaScript, community .NET and others</TD>
            </TR>
            <TR>
              <TD>
                <strong>WebSocket Streaming</strong>
              </TD>
              <TD>Via REST and MCP</TD>
              <TD>Yes, 50 symbols free, unlimited paid</TD>
            </TR>
          </TBody>
        </Table>

        <H3>Feature Checklist</H3>
        <Table>
          <THead>
            <TR>
              <TH>Capability</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>Typed client SDK</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>Consistent response shapes across endpoints</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Varies by asset class</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Tick-level and NBBO data</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Paid plans only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>SEC filings</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Limited</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Insider trading (Forms 3, 4, 5)</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Congressional focus</Partial>
              </TD>
            </TR>
            <TR>
              <TD>ESG and credit ratings</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>ESG only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Earnings transcripts</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>Standard rate limit response headers</TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>MCP server for AI agents</TD>
              <TD>
                <Yes>100+ tools</Yes>
              </TD>
              <TD>
                <Yes>70 tools</Yes>
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Real-Time Data</H2>
        <P>
          Real-time data is where Finnhub built its reputation. The free tier
          includes WebSocket access with up to 50 symbols, which is unusually
          generous for a no-cost plan. Paid tiers unlock unlimited symbols and
          access to tick-level data with 30+ years of history.
        </P>
        <P>
          AxionQuant approaches real-time data with a focus on reliability
          and consistency across asset classes. Quotes are available via REST
          and through the MCP server, with the same response shape whether
          you are fetching a stock, a crypto pair, or a forex rate. This
          matters for applications that need to mix asset classes without
          writing provider-specific parsing logic for each one.
        </P>

        <Chart>
          <ChartTitle>
            <Zap size={16} />
            Real-Time Data Capabilities
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>Full coverage</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Paid</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={90} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>Strong</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Free</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={45} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>US quotes only</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Real-Time Feature</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub Free</TH>
              <TH>Finnhub Paid</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Real-time quotes</strong>
              </TD>
              <TD>
                <Yes>All asset classes</Yes>
              </TD>
              <TD>
                <Yes>US stocks only</Yes>
              </TD>
              <TD>
                <Yes>Global coverage</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>WebSocket streaming</strong>
              </TD>
              <TD>Via REST and MCP</TD>
              <TD>
                <Yes>50 symbols</Yes>
              </TD>
              <TD>
                <Yes>Unlimited symbols</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Tick-level data</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>OHLCV history</strong>
              </TD>
              <TD>Deep multi-decade</TD>
              <TD>
                <No>Not available</No>
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Market status</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Latency</strong>
              </TD>
              <TD>Low latency across regions</TD>
              <TD>Standard</TD>
              <TD>Low latency</TD>
            </TR>
          </TBody>
        </Table>

        <Callout>
          <strong>Key difference:</strong> Finnhub free tier provides real-time
          quotes for US stocks only and does not include historical OHLCV
          data. AxionQuant free tier includes real-time quotes and historical
          price data across every supported asset class, which means you can
          build and test a complete strategy without upgrading.
        </Callout>

        <H3>Historical Price Data Depth</H3>
        <P>
          Historical depth determines what kinds of strategies you can
          backtest. Finnhub provides 30+ years of OHLCV history on paid
          plans, but this data is not available on the free tier. AxionQuant
          includes deep historical coverage from the free tier onward.
        </P>

        <Chart>
          <ChartTitle>
            <BarChart3 size={16} />
            Historical Price Data Access
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant Free</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>Deep multi-decade</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>AxionQuant Paid</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>Deep multi-decade</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Paid</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={85} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>30+ years</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Free</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={4} $color="#dc2626" />
            </ChartBarTrack>
            <ChartValue>Not available</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Historical Data</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub Free</TH>
              <TH>Finnhub Paid</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Daily OHLCV</strong>
              </TD>
              <TD>Deep multi-decade</TD>
              <TD>
                <No>Not available</No>
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Intraday OHLCV</strong>
              </TD>
              <TD>Deep coverage</TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Tick data history</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Survivorship-bias free</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>International OHLCV</strong>
              </TD>
              <TD>Growing coverage</TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>25+ years for TSX, LSE, Euronext</Yes>
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Fundamentals and Alternative Data</H2>
        <P>
          Both platforms offer fundamentals, but they organize the data
          differently. Finnhub bundles its fundamental data into a small
          number of endpoints with tiered access. AxionQuant exposes
          fundamentals as distinct tools, which makes them easier to query
          individually and combine with other data types.
        </P>

        <Chart>
          <ChartTitle>
            <Database size={16} />
            Data Category Coverage
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>14 categories</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub (All-in-One)</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={85} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>12 categories</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub (Free)</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={30} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>4 categories</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Data Category</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub Free</TH>
              <TH>Finnhub Paid</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Standardized financials</strong>
              </TD>
              <TD>
                <Yes>Income, balance, cash flow</Yes>
              </TD>
              <TD>
                <Partial>Basic metrics only</Partial>
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>SEC filings</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>Full list</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Insider transactions</strong>
              </TD>
              <TD>
                <Yes>Forms 3, 4, 5</Yes>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>With MSPR scoring</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Congressional trading</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>ESG scores</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Credit ratings</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Earnings transcripts</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>Separate purchase</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Analyst estimates</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
              <TD>
                <Yes>20+ years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>News and sentiment</strong>
              </TD>
              <TD>
                <Yes>Social, news, analyst</Yes>
              </TD>
              <TD>
                <Yes>1 year</Yes>
              </TD>
              <TD>
                <Yes>20 years</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Economic data</strong>
              </TD>
              <TD>
                <Yes>FRED sourced</Yes>
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>Historical</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>ETF holdings</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Options chains</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Institutional holdings (13F)</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>Full shareholder list</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Short interest</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
          </TBody>
        </Table>

        <H3>Alternative Data Comparison</H3>
        <P>
          Alternative data is the area where the two platforms diverge most
          sharply. Finnhub bundles congressional trading, lobbying records,
          government spending, and social sentiment into its All-in-One plan,
          but these datasets are unavailable on the free tier. AxionQuant
          includes SEC filings, insider trading, ESG, credit ratings, and
          sentiment from the free tier onward.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Alternative Data Source</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>SEC EDGAR filings (10-K, 10-Q, 8-K)</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Insider transactions (Forms 3, 4, 5)</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only, with MSPR scoring</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Congressional and senate trading</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>ESG scores and ratings</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Credit ratings</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>Institutional holdings (13F)</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Earnings call transcripts</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Separate purchase</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Social media sentiment</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Senate lobbying data</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>US government spending</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
            <TR>
              <TD>Short interest data</TD>
              <TD>
                <Yes>Free tier</Yes>
              </TD>
              <TD>
                <Partial>Paid only</Partial>
              </TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Pricing and Rate Limits</H2>
        <P>
          Finnhub pricing is structured around four tiers with a steep jump
          from the Professional plan to the All-in-One plan. The free tier
          is genuinely useful for prototyping, but the gap between what you
          can access for free and what you need for production is
          significant.
        </P>

        <Chart>
          <ChartTitle>
            <DollarSign size={16} />
            Monthly Pricing Comparison
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant Free</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={2} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>$0</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Free</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={2} $color="#16a34a" />
            </ChartBarTrack>
            <ChartValue>$0</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Starter</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={8} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>$11.99</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub Professional</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={25} $color="#f59e0b" />
            </ChartBarTrack>
            <ChartValue>$49.99</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub All-in-One</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#dc2626" />
            </ChartBarTrack>
            <ChartValue>$3,500</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Tier</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Free</strong>
              </TD>
              <TD>Generous daily budget with real-time and historical data</TD>
              <TD>60 requests per minute, US quotes only, no OHLCV</TD>
            </TR>
            <TR>
              <TD>
                <strong>Entry Paid</strong>
              </TD>
              <TD>Transparent tiers</TD>
              <TD>$11.99 per month (Starter), 300+ requests per minute</TD>
            </TR>
            <TR>
              <TD>
                <strong>Professional</strong>
              </TD>
              <TD>Published on pricing page</TD>
              <TD>$49.99 per month, 300+ requests per minute</TD>
            </TR>
            <TR>
              <TD>
                <strong>Enterprise</strong>
              </TD>
              <TD>Published on pricing page</TD>
              <TD>$3,500 per month, 900 market data calls per minute</TD>
            </TR>
            <TR>
              <TD>
                <strong>Billing frequency</strong>
              </TD>
              <TD>Monthly</TD>
              <TD>Annual (paid tiers)</TD>
            </TR>
            <TR>
              <TD>
                <strong>License</strong>
              </TD>
              <TD>Commercial</TD>
              <TD>Personal use on all tiers</TD>
            </TR>
          </TBody>
        </Table>

        <Callout>
          <strong>Licensing note:</strong> Finnhub terms specify personal use
          on all tiers, including the All-in-One plan. Commercial
          applications require contacting Finnhub directly. AxionQuant
          provides commercial licensing across its tiers. This is a critical
          distinction for any project intended for production or commercial
          deployment.
        </Callout>

        <H3>Rate Limit Comparison</H3>
        <Table>
          <THead>
            <TR>
              <TH>Metric</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub Free</TH>
              <TH>Finnhub Paid</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Requests per minute</strong>
              </TD>
              <TD>Generous free allowance</TD>
              <TD>60</TD>
              <TD>300+</TD>
            </TR>
            <TR>
              <TD>
                <strong>Requests per second</strong>
              </TD>
              <TD>Header-driven</TD>
              <TD>30 (global cap)</TD>
              <TD>30 (global cap)</TD>
            </TR>
            <TR>
              <TD>
                <strong>Overage behavior</strong>
              </TD>
              <TD>Headers report remaining budget</TD>
              <TD>HTTP 429, wait 1-2 seconds</TD>
              <TD>HTTP 429, wait 1-2 seconds</TD>
            </TR>
            <TR>
              <TD>
                <strong>WebSocket symbols</strong>
              </TD>
              <TD>Via REST and MCP</TD>
              <TD>50</TD>
              <TD>Unlimited</TD>
            </TR>
            <TR>
              <TD>
                <strong>OHLCV on free tier</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
              <TD>
                <Yes>30+ years</Yes>
              </TD>
            </TR>
          </TBody>
        </Table>

        <H3>Cost Over Time</H3>
        <P>
          The table below shows cumulative spend at the Professional tier
          over common time horizons, the tier most developers would need
          for production workloads.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Timeframe</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub Professional</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>1 month</TD>
              <TD>Free tier available</TD>
              <TD>$49.99</TD>
            </TR>
            <TR>
              <TD>6 months</TD>
              <TD>Free tier available</TD>
              <TD>$299.94</TD>
            </TR>
            <TR>
              <TD>12 months</TD>
              <TD>Free tier available, transparent upgrades</TD>
              <TD>$599.88</TD>
            </TR>
            <TR>
              <TD>24 months</TD>
              <TD>Scale only when you need to</TD>
              <TD>$1,199.76</TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Developer Experience</H2>
        <P>
          Finnhub earned its developer-friendly reputation for good reason.
          The API is well documented, the free tier is easy to start with,
          and the WebSocket implementation is straightforward. Official
          SDKs exist for Python and JavaScript, with community SDKs for
          other languages.
        </P>
        <P>
          AxionQuant ships an official TypeScript and JavaScript SDK with a
          typed client interface, plus a Python SDK. The SDK provides
          consistent method names and response shapes across every asset
          class, which reduces the cognitive overhead of working with
          multiple data types in a single application.
        </P>

        <Chart>
          <ChartTitle>
            <Code2 size={16} />
            Developer Experience Score
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>Excellent</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={82} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>Very good</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>Developer Feature</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Official SDK</strong>
              </TD>
              <TD>TypeScript, JavaScript, Python</TD>
              <TD>Python, JavaScript, .NET (community)</TD>
            </TR>
            <TR>
              <TD>
                <strong>Typed client</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Consistent response format</strong>
              </TD>
              <TD>
                <Yes>Across all asset classes</Yes>
              </TD>
              <TD>
                <Partial>Varies by asset class</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Rate limit headers</strong>
              </TD>
              <TD>
                <Yes>Standard</Yes>
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Proper HTTP status codes</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Multi-asset single client</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Endpoints split by asset class</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>WebSocket support</strong>
              </TD>
              <TD>Via REST and MCP</TD>
              <TD>
                <Yes />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Documentation quality</strong>
              </TD>
              <TD>Comprehensive, examples-first</TD>
              <TD>Structured, multi-language examples</TD>
            </TR>
          </TBody>
        </Table>

        <H3>Example: Fetching a Quote in Both SDKs</H3>
        <P>
          The code below shows how to fetch a real-time quote using each
          platform SDK. Both are clean, but note the difference in response
          consistency when you move between asset classes.
        </P>

        <CodeBlock>
          {`// AxionQuant SDK
const { Axion } = require('@axionquant/sdk');
const client = new Axion('your-api-key');

const quote = await client.stocks.quote('AAPL');
const cryptoQuote = await client.crypto.quote('BTC-USD');
// Both return the same response shape

// Finnhub SDK
const finnhub = require('finnhub');
const api_key = finnhub.ApiClient.instance.authentications['api_key'];
api_key.apiKey = "your-api-key";
const finnhubClient = new finnhub.DefaultApi();

finnhubClient.quote("AAPL", (error, data, response) => {
  console.log(data);
});
// Crypto and forex use different endpoints and response shapes`}
        </CodeBlock>
      </Section>

      <Section>
        <H2 className="serif">AI and Agent Integration</H2>
        <P>
          Both platforms have recognized that AI agents are becoming a
          primary consumer of financial data. Finnhub runs a hosted MCP
          server at mcp.finnhub.io that exposes 70 tools covering equities,
          funds, forex, crypto, economic data, and alternative datasets.
          AxionQuant offers an MCP server with 100+ tools that runs locally
          via npm and works with any MCP-compatible client.
        </P>

        <Chart>
          <ChartTitle>
            <Bot size={16} />
            MCP Tool Coverage
          </ChartTitle>

          <ChartRow>
            <ChartLabel>AxionQuant</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={100} $color="#0066cc" />
            </ChartBarTrack>
            <ChartValue>100+ tools</ChartValue>
          </ChartRow>

          <ChartRow>
            <ChartLabel>Finnhub</ChartLabel>
            <ChartBarTrack>
              <ChartBar $width={70} $color="#0ea5e9" />
            </ChartBarTrack>
            <ChartValue>70 tools</ChartValue>
          </ChartRow>
        </Chart>

        <Table>
          <THead>
            <TR>
              <TH>AI Integration</TH>
              <TH>AxionQuant</TH>
              <TH>Finnhub</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <strong>Official MCP server</strong>
              </TD>
              <TD>
                <Yes>Local via npm</Yes>
              </TD>
              <TD>
                <Yes>Hosted</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>MCP tool count</strong>
              </TD>
              <TD>100+</TD>
              <TD>70</TD>
            </TR>
            <TR>
              <TD>
                <strong>Natural-language queries</strong>
              </TD>
              <TD>
                <Yes>Via MCP</Yes>
              </TD>
              <TD>
                <Yes>Via MCP</Yes>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>SEC filings via MCP</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Insider trading via MCP</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Credit ratings via MCP</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <No />
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Agent-safe rate limits</strong>
              </TD>
              <TD>
                <Yes>Header-driven backoff</Yes>
              </TD>
              <TD>
                <Partial>HTTP 429 on free tier</Partial>
              </TD>
            </TR>
            <TR>
              <TD>
                <strong>Free tier MCP access</strong>
              </TD>
              <TD>
                <Yes />
              </TD>
              <TD>
                <Partial>Limited endpoints</Partial>
              </TD>
            </TR>
          </TBody>
        </Table>

        <H3>Example: MCP Configuration for AxionQuant</H3>
        <P>
          Setting up AxionQuant as an MCP server takes a single config block.
          The server runs locally and works with Claude Desktop and any
          MCP-compatible client.
        </P>

        <CodeBlock>
          {`{
  "mcpServers": {
    "axion-financial-data": {
      "command": "node",
      "args": ["/path/to/node_modules/@axionquant/mcp/index.js"],
      "env": { "API_KEY": "your_api_key_here" }
    }
  }
}`}
        </CodeBlock>

        <P>
          Once configured, an agent can answer questions like "What was
          Apple revenue last quarter?" or "Show me recent insider
          transactions for Tesla" by calling the appropriate tool and
          returning live data.
        </P>
      </Section>

      <Section>
        <H2 className="serif">Who Should Choose Which</H2>
        <P>
          Both platforms serve different needs. The table below maps common
          use cases to the platform that fits best.
        </P>

        <Table>
          <THead>
            <TR>
              <TH>Use Case</TH>
              <TH>Recommended Platform</TH>
              <TH>Why</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>
                <Search
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Quick prototype with real-time quotes
              </TD>
              <TD>Finnhub free tier</TD>
              <TD>60 per minute and WebSocket access for US stocks</TD>
            </TR>
            <TR>
              <TD>
                <TrendingUp
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Backtesting with historical OHLCV data
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                Finnhub free tier does not include OHLCV, and paid OHLCV
                starts at $11.99 per month
              </TD>
            </TR>
            <TR>
              <TD>
                <Database
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Production data pipeline with alternative data
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                SEC filings, insider trading, ESG, and credit ratings all
                available from free tier
              </TD>
            </TR>
            <TR>
              <TD>
                <Bot
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                LLM agent with financial tools
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                100+ MCP tools and agent-safe rate limits, with free tier
                access
              </TD>
            </TR>
            <TR>
              <TD>
                <FileText
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Congressional trading or lobbying research
              </TD>
              <TD>Finnhub paid</TD>
              <TD>
                Unique datasets, but require the All-in-One plan at $3,500
                per month
              </TD>
            </TR>
            <TR>
              <TD>
                <Globe
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                International equity coverage
              </TD>
              <TD>Finnhub paid</TD>
              <TD>
                70+ global exchanges, but only on paid tiers
              </TD>
            </TR>
            <TR>
              <TD>
                <Shield
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Commercial application
              </TD>
              <TD>AxionQuant</TD>
              <TD>
                Finnhub terms specify personal use on all tiers
              </TD>
            </TR>
            <TR>
              <TD>
                <Clock
                  size={14}
                  style={{ verticalAlign: 'middle', marginRight: 6 }}
                />
                Real-time WebSocket streaming
              </TD>
              <TD>Finnhub free tier</TD>
              <TD>50 symbols free, unlimited on paid</TD>
            </TR>
          </TBody>
        </Table>
      </Section>

      <Section>
        <H2 className="serif">Verdict</H2>
        <P>
          Finnhub is a strong choice for developers who need real-time US
          stock quotes and WebSocket streaming on a budget. The free tier is
          genuinely useful, and the $11.99 Starter plan unlocks OHLCV data at
          a price point that is hard to beat. For personal projects and
          prototyping, Finnhub is an excellent starting point.
        </P>
        <P>
          The limitations become apparent when your project moves toward
          production or commercial deployment. Finnhub free tier does not
          include historical OHLCV data, alternative datasets are locked
          behind the $3,500 All-in-One plan, and the terms specify personal
          use across all tiers. For a commercial application that needs a
          mix of price data, fundamentals, and alternative data, the cost
          and licensing constraints add up quickly.
        </P>
        <P>
          AxionQuant is built for the workflow that starts after the
          prototype. It offers:
        </P>

        <VerdictBox>
          <ul>
            <li>
              <strong>Real free-tier utility</strong> with real-time and
              historical data across every asset class
            </li>
            <li>
              <strong>Deep alternative data</strong> including SEC filings,
              insider trading, ESG, credit ratings, and sentiment, all
              available from the free tier
            </li>
            <li>
              <strong>A single SDK and API key</strong> for every asset class
              and data type
            </li>
            <li>
              <strong>An MCP server with 100+ tools</strong> that turns any
              LLM into a financial analyst with live data
            </li>
            <li>
              <strong>Commercial licensing</strong> across tiers, which
              Finnhub does not offer without direct negotiation
            </li>
            <li>
              <strong>Standard rate limit headers</strong> so your
              application can adapt instead of breaking
            </li>
          </ul>
        </VerdictBox>

        <P>
          If you are evaluating Finnhub for a project that needs to scale
          beyond personal use, AxionQuant is the platform that grows with
          you.
        </P>

        <CTALink href="https://axionquant.com/dashboard/api-keys">
          Start with a free API key
        </CTALink>
      </Section>

      <Section>
        <H2 className="serif">FAQ</H2>
        <FAQ
          faqs={[
            {
              question: 'Is AxionQuant a good alternative to Finnhub?',
              answer:
                'Yes. AxionQuant offers real-time and historical data across stocks, crypto, forex, futures, indices, and ETFs, plus alternative data including SEC filings, insider trading, ESG, credit ratings, and sentiment. Unlike Finnhub, these categories are available from the free tier onward. AxionQuant also offers commercial licensing across its tiers, while Finnhub terms specify personal use on all plans.',
            },
            {
              question: 'What is the difference between Finnhub free and paid?',
              answer:
                'Finnhub free tier includes 60 requests per minute, US real-time quotes, company profiles, basic financial metrics, and WebSocket access for 50 symbols. It does not include historical OHLCV data. Paid tiers start at $11.99 per month for Starter and unlock OHLCV history, international coverage, and additional datasets. The All-in-One plan at $3,500 per month includes congressional trading, lobbying records, ESG scores, and earnings transcripts.',
            },
            {
              question: 'Does Finnhub offer historical stock prices on the free tier?',
              answer:
                'No. Finnhub free tier does not include OHLCV historical price data. Historical candles are only available on paid plans starting at $11.99 per month. This is a significant limitation for backtesting or any application that needs historical data, and it is one of the main reasons developers evaluate alternatives like AxionQuant, which includes deep historical coverage from the free tier.',
            },
            {
              question:
                'Which platform has better alternative data coverage?',
              answer:
                'Both platforms offer strong alternative data, but they organize it differently. Finnhub bundles congressional trading, lobbying records, government spending, ESG scores, and social sentiment into its All-in-One plan at $3,500 per month. AxionQuant includes SEC filings, insider trading, ESG, credit ratings, institutional holdings, earnings transcripts, and sentiment from the free tier onward. For developers who need alternative data without the enterprise price tag, AxionQuant is the more accessible option.',
            },
            {
              question: 'Does Finnhub offer an MCP server for AI agents?',
              answer:
                'Yes. Finnhub runs a hosted MCP server at mcp.finnhub.io that exposes 70 tools covering equities, funds, forex, crypto, economic data, and alternative datasets. AxionQuant offers an MCP server with 100+ tools that runs locally via npm and works with any MCP-compatible client. AxionQuant MCP server includes SEC filings, insider trading, and credit ratings, which are not available through Finnhub MCP.',
            },
            {
              question: 'Can I use Finnhub for a commercial application?',
              answer:
                'Finnhub terms of service specify personal use on all tiers, including the All-in-One plan. Commercial applications require contacting Finnhub directly to negotiate a commercial license. AxionQuant provides commercial licensing across its tiers, which makes it a simpler choice for production or commercial deployment.',
            },
            {
              question: 'How do the SDKs compare between Finnhub and AxionQuant?',
              answer:
                'Finnhub offers official SDKs for Python and JavaScript, with community SDKs for .NET and other languages. AxionQuant ships an official TypeScript and JavaScript SDK with a typed client interface, plus a Python SDK. The key difference is consistency: AxionQuant SDK provides the same method names and response shapes across every asset class, while Finnhub endpoints and response structures vary depending on whether you are fetching a stock, crypto, or forex quote.',
            },
            {
              question:
                'Which platform is better for WebSocket streaming?',
              answer:
                'Finnhub offers WebSocket streaming with 50 symbols on the free tier and unlimited symbols on paid plans. This is one of Finnhub strongest features. AxionQuant provides real-time data via REST and MCP rather than a dedicated WebSocket connection. If your application requires a persistent WebSocket stream, Finnhub is the stronger choice. If you need REST-based real-time quotes with consistent response shapes across asset classes, AxionQuant is the better fit.',
            },
            {
              question: 'Does AxionQuant offer international stock data?',
              answer:
                'AxionQuant covers US equities with deep historical data and growing international coverage. Finnhub offers international coverage across 70+ exchanges on paid tiers, with 25+ years of OHLCV history for TSX, LSE, Euronext, and Deutsche Börse. For applications that need broad international coverage, Finnhub paid tiers are the stronger option. For US-focused applications, AxionQuant provides deep coverage with the advantage of alternative data included.',
            },
            {
              question: 'How do I migrate from Finnhub to AxionQuant?',
              answer:
                'Migration is straightforward for price and fundamentals data. Both platforms provide OHLCV series, company profiles, and financial statements. You will typically replace the HTTP call and reshape the response once. AxionQuant SDK provides typed methods for every asset class with consistent response shapes, which simplifies the migration compared to Finnhub variable response formats across asset classes.',
            },
          ]}
          schema
        />
      </Section>
    </>
  );
}
